import { ArrowForward } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  ChartsTooltipContainer,
  useItemTooltip,
} from '@mui/x-charts/ChartsTooltip';
import { Show, useIsMobile } from '@side/pantry';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useEffectOnce } from 'react-use';
import type { InteractionType } from '@/common/types/interaction';
import {
  getInteractionsFiltersKey,
  interactionsAgentsState,
  interactionsStatusesState,
  useSetDateCreatedFilterState,
} from '@/views/Interactions/state/filters';
import {
  TrackHomePageEvent,
  useTrackHomePageAnalytics,
} from '$analytics/useTrackAnalytics/domains/homePage';
import { getLabel } from '../utils';
import { useFactsAndFiguresContext } from './FactsAndFiguresContext';

export function PieChartTooltip() {
  const tooltipData = useItemTooltip<'pie'>();
  const { value: tooltipValue, identifier } = tooltipData ?? {};
  const { id, value, label } = tooltipValue ?? {};
  const { seriesId: interactionType } = identifier ?? {};

  const isMobile = useIsMobile('sm');
  const router = useRouter();
  const { agents, dateRange, startDate, endDate } = useFactsAndFiguresContext();
  const stateKey = getInteractionsFiltersKey(
    interactionType as InteractionType,
  );
  const updateDateCreatedFilter = useSetDateCreatedFilterState(stateKey);
  const setStatuses = useSetAtom(interactionsStatusesState(stateKey));
  const setAgentFilters = useSetAtom(interactionsAgentsState(stateKey));

  const trackEvent = useTrackHomePageAnalytics();
  useEffectOnce(() => {
    if (!isMobile) {
      trackEvent(TrackHomePageEvent.FactsAndFiguresSegmentHover, {
        numItem: value,
        interactionType,
        interactionStatus: label,
      });
    }
  });

  const routeToTLVPageWithFiltersApplied = useCallback(() => {
    // Status Filters
    if (label) {
      setStatuses([
        {
          label: typeof label === 'string' ? label : label('arc'),
          value: `status:${id}`,
        },
      ]);
    } else {
      setStatuses([]);
    }

    // Date Filters
    updateDateCreatedFilter({
      dateRange,
      startDate,
      endDate,
    });

    // Agent Filters
    setAgentFilters(
      agents.map((agent) => ({
        label: agent.label,
        value: `teamMemberId:${agent.value}`,
      })),
    );

    trackEvent(
      TrackHomePageEvent.FactsAndFiguresMobileSegmentTooltipViewAllButtonClick,
      {
        numItem: value,
        interactionType,
        interactionStatus: label,
        dateType: dateRange,
        startDate,
        endDate,
        agentId: agents.map((agent) => agent.value).join(','),
      },
    );

    void router.push(`/${interactionType}s`);
  }, [
    agents,
    dateRange,
    endDate,
    id,
    interactionType,
    label,
    router,
    setAgentFilters,
    setStatuses,
    startDate,
    trackEvent,
    updateDateCreatedFilter,
    value,
  ]);

  const labelString = getLabel(label);
  return (
    <ChartsTooltipContainer trigger="item">
      <Box
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 0.5,
          boxShadow: (theme) => theme.shadows[3],
          marginLeft: 2,
          marginTop: 2,
          paddingX: 1,
          paddingY: 0.5,
          pointerEvents: 'all',
          width: 'fit-content',
        }}
      >
        <Stack
          sx={{
            color: 'text.secondary',
            flexDirection: 'row',
            gap: 1.5,
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="body3">{labelString}</Typography>

          <Typography variant="subtitle2">{value}</Typography>
        </Stack>

        <Show when={isMobile}>
          <Stack
            sx={{
              flexDirection: 'row',
              gap: 0,
              height: 20,
              justifyContent: 'end',
              marginTop: 1,
              paddingLeft: 4,
              width: '100%',
            }}
          >
            <Button
              onClick={routeToTLVPageWithFiltersApplied}
              sx={{
                color: 'text.secondary',
                fontSize: 13,
                minWidth: 'unset',
                paddingLeft: 0.5,
              }}
              variant="link"
            >
              View all
              <ArrowForward
                sx={{ color: 'text.secondary', height: 20, width: 20 }}
              />
            </Button>
          </Stack>
        </Show>
      </Box>
    </ChartsTooltipContainer>
  );
}
